import { useSelector, useDispatch } from 'react-redux'
import type { RootState, AppDispatch } from '@/store'
import {
  loginThunk,
  logoutThunk,
  updateUser,
  verifyMagicLinkThunk,
  requestMagicLinkThunk,
} from '@/redux/userSlice'

/**
 * Custom hook for auth operations backed by Redux store.
 * Provides a clean interface to auth state and actions.
 */
export const useAuth = () => {
  const marketer = useSelector((state: RootState) => state.user.currentUser)
  const isLoading = useSelector((state: RootState) => state.user.loading)
  const error = useSelector((state: RootState) => state.user.error)
  const dispatch = useDispatch<AppDispatch>()

  const login = async (credentials: { email: string; password: string }) => {
    await dispatch(loginThunk(credentials))
  }

  const logout = () => {
    dispatch(logoutThunk())
  }

  const connectSocialMedia = (platform: string) => {
    if (!marketer) return
    dispatch(
      updateUser({
        connectedAccounts: {
          ...marketer.connectedAccounts,
          [platform]: true,
        },
      }),
    )
  }

  const disconnectSocialMedia = (platform: string) => {
    if (!marketer) return
    dispatch(
      updateUser({
        connectedAccounts: {
          ...marketer.connectedAccounts,
          [platform]: false,
        },
      }),
    )
  }

  const updatePreferences = (field: string, values: string[]) => {
    if (!marketer) return
    dispatch(
      updateUser({
        preferences: {
          ...marketer.preferences,
          [field]: values,
        },
      }),
    )
  }

  const verifyMagicLink = async (token: string) => {
    const result = await dispatch(verifyMagicLinkThunk(token))
    if (verifyMagicLinkThunk.rejected.match(result)) {
      throw new Error((result.payload as string) || 'Magic link verification failed')
    }
    return result.payload
  }

  const requestMagicLink = async (email: string) => {
    await dispatch(requestMagicLinkThunk(email))
  }

  return {
    marketer,
    isLoading,
    error,
    login,
    logout,
    connectSocialMedia,
    disconnectSocialMedia,
    updatePreferences,
    verifyMagicLink,
    requestMagicLink,
  }
}
